"use client";

import React, { useMemo } from "react";
import { Label, PolarRadiusAxis, RadialBar, RadialBarChart } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  cn,
  ChartConfig,
} from "visuais";

interface RateioRadialChartProps {
  data: Array<{ [key: string]: number }>;
  config: ChartConfig;
  total: number;
  label: string;
  className?: string;
}

export function RateioRadialChart({
  data,
  config,
  total,
  label,
  className,
}: RateioRadialChartProps) {
  if (data.length === 0 || total === 0) {
    return (
      <div className={cn("flex items-center justify-center", className)}>
        <div className="text-xs text-muted-foreground text-center">
          <div className="w-12 h-12 border-2 border-muted-foreground/30 rounded-full flex items-center justify-center mb-2 mx-auto">
            <span className="text-muted-foreground/50 text-base">-</span>
          </div>
          <div>Sem dados</div>
        </div>
      </div>
    );
  }

  // Calcula o percentual total dos dados
  const totalData = Object.values(data[0]).reduce(
    (sum, value) => sum + value,
    0
  );
  const percentual = total > 0 ? (totalData / total) * 100 : 0;

  return (
    <div className={cn("flex flex-col h-full", className)}>
      <div className="text-sm text-center font-medium text-cyan-700 dark:text-cyan-400 mb-1">
        {label}
      </div>
      <div className="flex-1 flex items-center justify-center">
        <ChartContainer
          config={config}
          className="w-full h-full min-w-[240px] min-h-[160px]"
        >
          <RadialBarChart
            data={data}
            endAngle={180}
            innerRadius={60}
            outerRadius={90}
            barGap={4}
          >
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
            />
            <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
              <Label
                content={({ viewBox }) => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    return (
                      <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) - 8}
                          className="fill-foreground text-sm font-bold"
                        >
                          {percentual.toFixed(0)}%
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </PolarRadiusAxis>
            {Object.keys(config).map((key, index) => (
              <RadialBar
                key={key}
                dataKey={key}
                stackId="a"
                cornerRadius={6}
                fill={`var(--color-${key})`}
                className="stroke-transparent stroke-2"
              />
            ))}
          </RadialBarChart>
        </ChartContainer>
      </div>
    </div>
  );
}
